import { CONTEXT_LESSONS_ITEMS, STATUS_TEXT } from 'constants/common';

import type {
  CourseLessonRow,
  LinkedCourse,
  LinkedSchool,
} from 'store/courses-lessons/helpers';

import { getTranslation } from 'helpers/getTranslation';

import { getCourseType } from 'components/CoursesTable/CoursesTable.helpers';

import {
  GROUP_BY_COURSE,
  GROUP_BY_LESSON_TYPE,
  GROUP_BY_NONE,
  GROUP_BY_STATUS,
  type GroupByValue,
} from './CoursesLessonsPage.grouping.config';

export type DisplayRow = CourseLessonRow & {
  _rowKey: string;
  groupCourse?: LinkedCourse;
};

export type LessonGroup = {
  id: string;
  label: string;
  preview?: string;
  courseType?: string;
  school?: LinkedSchool;
  rows: DisplayRow[];
};

const UNLINKED_GROUP_ID = '__unlinked__';

const getLessonTypeLabel = (lessonType: string) =>
  CONTEXT_LESSONS_ITEMS.find((option) => option.value === lessonType)?.text ||
  lessonType;

const getStatusLabel = (status: string) => {
  const normalizedStatus = status?.toLowerCase() as keyof typeof STATUS_TEXT;

  return STATUS_TEXT[normalizedStatus] || status;
};

const createDisplayRow = (
  lesson: CourseLessonRow,
  rowKey: string,
  groupCourse?: LinkedCourse,
): DisplayRow => ({
  ...lesson,
  _rowKey: rowKey,
  groupCourse,
});

const sortGroupsByLabel = (groups: LessonGroup[]) =>
  [...groups].sort((leftGroup, rightGroup) =>
    leftGroup.label.localeCompare(rightGroup.label, undefined, {
      sensitivity: 'base',
    }),
  );

const PLACEMENT_SORT_FALLBACK = Number.MAX_SAFE_INTEGER;

const getPlacementSortCourse = (
  row: DisplayRow,
  groupCourseId?: string,
): LinkedCourse | undefined => {
  if (row.groupCourse) {
    return row.groupCourse;
  }

  if (groupCourseId) {
    return row.courses.find((course) => course.id === groupCourseId);
  }

  return row.courses[0];
};

const sortGroupRowsByPlacement = (
  rows: DisplayRow[],
  groupCourseId?: string,
): DisplayRow[] =>
  [...rows].sort((leftRow, rightRow) => {
    const leftCourse = getPlacementSortCourse(leftRow, groupCourseId);
    const rightCourse = getPlacementSortCourse(rightRow, groupCourseId);

    const leftModuleOrder = leftCourse?.moduleOrder ?? PLACEMENT_SORT_FALLBACK;
    const rightModuleOrder = rightCourse?.moduleOrder ?? PLACEMENT_SORT_FALLBACK;

    if (leftModuleOrder !== rightModuleOrder) {
      return leftModuleOrder - rightModuleOrder;
    }

    const leftOrder = leftCourse?.order ?? PLACEMENT_SORT_FALLBACK;
    const rightOrder = rightCourse?.order ?? PLACEMENT_SORT_FALLBACK;

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return (leftRow.title || '').localeCompare(rightRow.title || '', undefined, {
      sensitivity: 'base',
    });
  });

const buildCourseGroups = (lessons: CourseLessonRow[]): LessonGroup[] => {
  const groupsMap = new Map<string, LessonGroup>();

  for (const lesson of lessons) {
    if (!lesson.courses.length) {
      const unlinkedGroup = groupsMap.get(UNLINKED_GROUP_ID) ?? {
        id: UNLINKED_GROUP_ID,
        label: getTranslation('courses_lessons_page.group_by.unlinked_course'),
        rows: [],
      };

      unlinkedGroup.rows.push(createDisplayRow(lesson, `${lesson.id}-unlinked`));
      groupsMap.set(UNLINKED_GROUP_ID, unlinkedGroup);
      continue;
    }

    for (const course of lesson.courses) {
      const existingGroup = groupsMap.get(course.id);

      if (existingGroup) {
        existingGroup.rows.push(
          createDisplayRow(lesson, `${lesson.id}-${course.id}`, course),
        );
        continue;
      }

      groupsMap.set(course.id, {
        id: course.id,
        label: course.title,
        preview: course.preview,
        courseType: getCourseType(course.type),
        school: course.school,
        rows: [createDisplayRow(lesson, `${lesson.id}-${course.id}`, course)],
      });
    }
  }

  return sortGroupsByLabel(
    Array.from(groupsMap.values()).map((group) => ({
      ...group,
      rows:
        group.id === UNLINKED_GROUP_ID
          ? group.rows
          : sortGroupRowsByPlacement(group.rows, group.id),
    })),
  );
};

const buildLessonTypeGroups = (lessons: CourseLessonRow[]): LessonGroup[] => {
  const groupsMap = new Map<string, LessonGroup>();

  for (const lesson of lessons) {
    const groupId = lesson.type || '__unknown_type__';
    const existingGroup = groupsMap.get(groupId);

    if (existingGroup) {
      existingGroup.rows.push(createDisplayRow(lesson, lesson.id));
      continue;
    }

    groupsMap.set(groupId, {
      id: groupId,
      label: getLessonTypeLabel(lesson.type),
      rows: [createDisplayRow(lesson, lesson.id)],
    });
  }

  return sortGroupsByLabel(
    Array.from(groupsMap.values()).map((group) => ({
      ...group,
      rows: sortGroupRowsByPlacement(group.rows),
    })),
  );
};

const buildStatusGroups = (lessons: CourseLessonRow[]): LessonGroup[] => {
  const groupsMap = new Map<string, LessonGroup>();

  for (const lesson of lessons) {
    const groupId = lesson.status || '__unknown_status__';
    const existingGroup = groupsMap.get(groupId);

    if (existingGroup) {
      existingGroup.rows.push(createDisplayRow(lesson, lesson.id));
      continue;
    }

    groupsMap.set(groupId, {
      id: groupId,
      label: getStatusLabel(lesson.status),
      rows: [createDisplayRow(lesson, lesson.id)],
    });
  }

  return sortGroupsByLabel(
    Array.from(groupsMap.values()).map((group) => ({
      ...group,
      rows: sortGroupRowsByPlacement(group.rows),
    })),
  );
};

export const buildLessonGroups = (
  lessons: CourseLessonRow[],
  groupBy: GroupByValue,
): LessonGroup[] => {
  if (groupBy === GROUP_BY_NONE) {
    return [
      {
        id: 'all',
        label: '',
        rows: sortGroupRowsByPlacement(
          lessons.map((lesson) => createDisplayRow(lesson, lesson.id)),
        ),
      },
    ];
  }

  if (groupBy === GROUP_BY_COURSE) {
    return buildCourseGroups(lessons);
  }

  if (groupBy === GROUP_BY_LESSON_TYPE) {
    return buildLessonTypeGroups(lessons);
  }

  if (groupBy === GROUP_BY_STATUS) {
    return buildStatusGroups(lessons);
  }

  return [];
};

export const shouldShowGroupHeaders = (groupBy: GroupByValue) =>
  groupBy !== GROUP_BY_NONE;
